import { Dispatch, SetStateAction, useState } from "react";
import { Phone, ArrowUpRight, ArrowDownLeft } from "lucide-react";
import { CallLogsState } from "src/interfaces";
import Table from "src/base-components/Table";
import Card from "src/components/Card";
import LoadingIcon from "src/base-components/LoadingIcon";
import Pagination from "src/base-components/Pagination";
// import moment from "moment";
import { useNavigate } from "react-router-dom";
import { userTimeZone } from "src/components/CallsLogsModal/constants";
import PostCallRemarks from "src/themes/Razor/CallLogPanel/PostCallRemarks";


type Props = {
  callLogs: CallLogsState;
  handleRowClick: (id: string | number, session_id?: string | null) => void;
  getStatusColor: (status: string) => string;
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
};

export default function CallLogsTable({
  callLogs,
  handleRowClick,
  getStatusColor,
  currentPage,
  setCurrentPage,
  pageSize,
  setPageSize,
}: Props) {
  const loading = callLogs?.loading ?? false;
  const totalPages = callLogs?.data?.last_page || 1;
  const totalItem = callLogs?.data?.total;

  const paginatedData = callLogs?.data?.data || [];


  const [openPostCall, setOpenPostCall] = useState(false);
  const [getCallerNum, setGetCallerNum] = useState<string | null>(null);
  const [direction, setDirection] = useState<string | null>(null);
  const [getSessionID, setGetSessionID] = useState<string | null>(null);



 
  const navigate = useNavigate();
  
  const handleClose = () => {
    setOpenPostCall(false);
    setGetCallerNum(null);
    setGetSessionID(null);
    setDirection(null);
  };

  const handlePageClick = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleFirstPage = () => setCurrentPage(1);
  const handleLastPage = () => setCurrentPage(totalPages);
  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return "--";

    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const renderCommaPills = (value) => {
    if (!value || typeof value !== "string") {
      return <span className="text-gray-400">--</span>;
    }

    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item, index) => (
        <span
          key={index}
          className="inline-flex items-center justify-center 
                   min-w-[22px] h-[22px] 
                   rounded-full 
                   bg-gray-200 text-black 
                   text-xs font-semibold 
                   mr-1  shadow-2xl border border-gray-300 "
        >
          {item}
        </span>
      ));
  };

  return (
    <>
      <Card className={`col-span-12 relative`}>
        <div className="overflow-auto lg:overflow-visible">
          <div className="p-3">
            <Table className="w-full relative bg-white">
              <Table.Thead>
                <Table.Tr>
                  {[
                    "Call Direction",
                    " Queue Number",
                    "Customer / Phone",
                    "User",
                    "Date & Time",
                    "Duration (mm:ss)",

                    // "OutCome",
                    "Query",
                    "Remarks",
                  ].map((title, index) => (
                    <Table.Th
                      key={index}
                      className="py-4 font-medium bg-inherit text-slate-500 text-start"
                    >
                      {title}
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>

              <Table.Tbody className="bg-inherit">
                {loading ? (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <div className="flex justify-center items-center w-full">
                        <LoadingIcon
                          color="#64748b"
                          icon="oval"
                          className="w-8 h-8 mx-auto"
                        />
                      </div>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  <>
                    {paginatedData && paginatedData.length > 0 ? (
                      paginatedData.map((call, rowIndex) => {
                        // const formattedTime = call?.event_time
                        //   ? moment(
                        //       call?.event_time,
                        //       "YYYY-MM-DD HH:mm:ss"
                        //     ).format("MMM DD, hh:mm A")
                        //   : "--";

                        const formattedTime =
                          call?.event_time && userTimeZone(call?.event_time);
                        return (
                          <Table.Tr
                            className=" relative"
                            key={rowIndex}
                          >
                            <Table.Td
                              className="py-4 border-dashed dark:bg-darkmode-600"
                              onClick={() =>
                                handleRowClick(call.id, call?.session_id)
                              }
                            >
                              <div className="flex items-center space-x-2">
                                <div className="rounded-lg relative flex items-center justify-center">
                                  <Phone
                                    className={`w-5 h-5 ${
                                      call.direction !== "Outbound"
                                        ? "text-blue-600"
                                        : "text-green-500"
                                    }`}
                                  />
                                  {call.direction === "Outbound" && (
                                    <ArrowUpRight className="w-3.5 h-3.5 text-green-500 absolute -right-1 bottom-[8px] rotate-12" />
                                  )}
                                  {call.direction !== "Outbound" && (
                                    <ArrowDownLeft className="w-3.5 h-3.5 text-blue-600 absolute -right-1 bottom-[8px] rotate-12" />
                                  )}
                                </div>
                                <p className="font-medium text-gray-900">
                                  {call.direction || "--"}
                                </p>
                              </div>
                            </Table.Td>
                            <Table.Td
                              className="py-4 border-dashed dark:bg-darkmode-600"
                              onClick={() =>
                                handleRowClick(call.id, call?.session_id)
                              }
                            >
                              <p className="font-medium text-gray-900">
                                {call?.call_queue_name}
                              </p>
                              <p className="font-medium text-gray-900">
                                {renderCommaPills(
                                  call?.input_pressed_by_customer,
                                )}
                              </p>
                            </Table.Td>
                            <Table.Td
                              className="py-4 border-dashed dark:bg-darkmode-600"
                              onClick={() =>
                                handleRowClick(call.id, call?.session_id)
                              }
                            >
                              <p className="font-medium text-gray-900">
                                {call.customer_info?.fullname || "--"}
                              </p>
                              <p className="text-sm text-gray-500">
                                {call.direction === "Outbound"
                                  ? call.to_number
                                  : call.from_number}
                              </p>
                            </Table.Td>
                            <Table.Td
                              className="py-4 border-dashed dark:bg-darkmode-600 text-gray-600"
                              onClick={() =>
                                handleRowClick(call.id, call?.session_id)
                              }
                            >
                              <p>{call?.agent_name}</p>
                              {call?.agent_id ? <p>#{call.agent_id}</p> : <p>No User Assigned</p>}
                            </Table.Td>
                            <Table.Td
                              className="py-4 border-dashed dark:bg-darkmode-600 text-gray-600"
                              onClick={() =>
                                handleRowClick(call.id, call?.session_id)
                              }
                            >
                              {formattedTime ? formattedTime?.dateTime : "--"}
                            </Table.Td>
                            <Table.Td
                              className="py-4 border-dashed dark:bg-darkmode-600 text-gray-600"
                              onClick={() =>
                                handleRowClick(call.id, call?.session_id)
                              }
                            >
                              {formatDuration(call?.duration_seconds)}
                            </Table.Td>
                            {/* <Table.Td
                              className="py-4 border-dashed dark:bg-darkmode-600"
                              onClick={() =>
                                handleRowClick(call.id, call?.session_id)
                              }
                            >
                              <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                                  call.status
                                )}`}
                              >
                                {call.status}
                              </span>
                            </Table.Td> */}
                            {/* <Table.Td
                              className="py-4 border-dashed dark:bg-darkmode-600"
                              onClick={() =>
                                handleRowClick(call.id, call?.session_id)
                              }
                            >
                              {call?.call_outcome || call?.remarks ? (
                                <>
                                  {call?.call_outcome && (
                                    <p className="text-[#475569] capitalize font-medium">
                                      {call.call_outcome}
                                    </p>
                                  )}
                                  {call?.remarks && (
                                    <p
                                      className="truncate max-w-[200px]"
                                      title={call?.remarks}
                                    >
                                      {call.remarks}
                                    </p>
                                  )}
                                </>
                              ) : (
                                "--"
                              )}
                            </Table.Td> */}
                            <Table.Td className="py-4 border-dashed dark:bg-darkmode-600">
                              {call?.query_info?.query_no ? (
                                <span
                                  className="text-blue-600 font-medium cursor-pointer"
                                  onClick={() => {
                                    navigate(
                                      "/query-detail/" + call?.query_info?.id,
                                    );
                                  }}
                                >
                                  {call.query_info.query_no}
                                </span>
                              ) : (
                                <span className="text-gray-400">No query</span>
                              )}
                            </Table.Td>
                            <Table.Td 
                              className={`py-4 border-dashed dark:bg-darkmode-600 text-gray-600 ${call?.remarks == null ? "cursor-pointer" : ""}`}
                              onClick={() => {
                                if (call?.remarks != null) return;
                                setOpenPostCall(true);
                                setGetCallerNum(call.direction === "Outbound" ? call.to_number : call.from_number);
                                setDirection((call.direction as string ));
                                setGetSessionID((call?.session_id as string));
                              }}
                            >
                              <div
                                className={
                                  call?.remarks == null
                                    ? "text-[#FF6900]"
                                    : "text-gray-600"
                                }
                              >
                                {call?.remarks || "Pending"}
                              </div>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })
                    ) : (
                      <div className="w-full py-10 text-center bg-inherit">
                        <div className="absolute left-0 right-0 font-medium text-slate-500">
                          No data available
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Table.Tbody>
            </Table>
          </div>
        </div>
      </Card>
      <Card className="flex flex-col-reverse flex-wrap items-center flex-reverse mt-8 sm:flex-row p-5">
        <Pagination
          className="flex-1 w-full mr-auto sm:w-auto"
          totalItems={totalItem}
          rowsPerPage={pageSize}
          currentPage={currentPage}
          onPageClick={handlePageClick}
          onNextClick={handleNextPage}
          onPrevClick={handlePrevPage}
          onLastClick={handleLastPage}
          onFirstClick={handleFirstPage}
          showColors={false}
        />
        {/* <select
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setCurrentPage(1);
          }}
          className="h-10 px-4 pr-8 border border-gray-300 rounded-xl text-gray-600 bg-white"
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={30}>30</option>
        </select> */}
      </Card>
      
      <PostCallRemarks
        visible={openPostCall}
        onClose={handleClose}
        getCallerNum={getCallerNum}
        direction={direction}
        getSessionID={getSessionID}
      />
    </>
  );
}
